import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", fontFamily: "inherit",
};

function GroupeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id && id !== "nouveau");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [eleves, setEleves] = useState([]);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    nom: "", description: "", type: "permanent", eleves_ids: [],
  });

  useEffect(() => {
    api.get("/eleves").then(({ data }) => setEleves(data));
    if (isEdit) {
      api.get(`/groupes/${id}`).then(({ data }) => {
        setForm({
          nom: data.nom || "",
          description: data.description || "",
          type: data.type || "permanent",
          eleves_ids: data.eleves?.map((e) => e.id) || [],
        });
      });
    }
  }, [id, isEdit]);

  const toggleEleve = (eleveId) => {
    setForm((prev) => ({
      ...prev,
      eleves_ids: prev.eleves_ids.includes(eleveId)
        ? prev.eleves_ids.filter((x) => x !== eleveId)
        : [...prev.eleves_ids, eleveId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) {
      setError("Le nom du groupe est obligatoire");
      return;
    }
    setError("");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/groupes/${id}`, form);
      } else {
        await api.post("/groupes", form);
      }
      navigate("/groupes");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const filteredEleves = eleves.filter((e) =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase()) ||
    e.ville?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>

            <MDBox mb={3} display="flex" alignItems="center" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/groupes")}
                sx={{ borderRadius: "10px", px: 2, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Retour
              </MDButton>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767" }}>
                {isEdit ? "Modifier le groupe" : "Créer un groupe"}
              </MDTypography>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={2} px={3} sx={{
                background: "linear-gradient(135deg, #00695c, #00897b)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,105,92,0.4)",
              }}>
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  Informations du groupe
                </MDTypography>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>
                {error && (
                  <MDBox mb={3} p={2} sx={{
                    background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2",
                  }}>
                    <MDTypography variant="caption" color="error" fontWeight="medium">
                      ⚠️ {error}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox component="form" onSubmit={handleSubmit}>

                  {/* Nom */}
                  <MDBox mb={4}>
                    <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                      <MDBox sx={{
                        width: 32, height: 32, borderRadius: "8px",
                        background: "linear-gradient(135deg, #00695c, #00897b)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Icon sx={{ color: "#fff", fontSize: 16 }}>group</Icon>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
                        Informations générales
                      </MDTypography>
                    </MDBox>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{
                          textTransform: "uppercase", fontSize: 11,
                          letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                        }}>
                          Nom du groupe *
                        </MDTypography>
                        <input value={form.nom}
                          onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                          placeholder="Ex: Groupe débutants vendredi..."
                          style={fieldStyle} autoComplete="off"
                          onFocus={(e) => (e.target.style.borderColor = "#00695c")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{
                          textTransform: "uppercase", fontSize: 11,
                          letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                        }}>
                          Type
                        </MDTypography>
                        <MDBox display="flex" gap={1}>
                          {[
                            { val: "permanent", label: "Permanent", icon: "groups" },
                            { val: "ponctuel", label: "Ponctuel", icon: "group_work" },
                          ].map((opt) => (
                            <MDBox key={opt.val}
                              onClick={() => setForm((p) => ({ ...p, type: opt.val }))}
                              sx={{
                                flex: 1, height: 44, borderRadius: "8px",
                                border: "1px solid",
                                borderColor: form.type === opt.val ? "#00695c" : "#d2d6da",
                                background: form.type === opt.val ? "#00695c" : "#fff",
                                color: form.type === opt.val ? "#fff" : "#344767",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                gap: 0.5, cursor: "pointer", fontSize: 12, fontWeight: 600,
                                transition: "all 0.2s",
                              }}>
                              <Icon sx={{ fontSize: 16 }}>{opt.icon}</Icon>
                              {opt.label}
                            </MDBox>
                          ))}
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{
                          textTransform: "uppercase", fontSize: 11,
                          letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                        }}>
                          Description
                        </MDTypography>
                        <textarea value={form.description}
                          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                          placeholder="Description du groupe..."
                          rows={2}
                          style={{
                            ...fieldStyle, height: "auto",
                            padding: "10px 14px", resize: "vertical",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "#00695c")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Sélection élèves */}
                  <MDBox mb={4}>
                    <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                      <MDBox sx={{
                        width: 32, height: 32, borderRadius: "8px",
                        background: "linear-gradient(135deg, #00695c, #00897b)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Icon sx={{ color: "#fff", fontSize: 16 }}>school</Icon>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
                        Élèves du groupe
                        {form.eleves_ids.length > 0 && (
                          <MDBox component="span" sx={{
                            ml: 1, px: 1, py: 0.2, borderRadius: "10px",
                            background: "#00695c", color: "#fff",
                            fontSize: 12, fontWeight: 700,
                          }}>
                            {form.eleves_ids.length} sélectionné{form.eleves_ids.length > 1 ? "s" : ""}
                          </MDBox>
                        )}
                      </MDTypography>
                    </MDBox>
                    <Divider sx={{ mb: 2 }} />

                    {/* Recherche élèves */}
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="🔍 Rechercher un élève..."
                      style={{ ...fieldStyle, mb: 2 }}
                      onFocus={(e) => (e.target.style.borderColor = "#00695c")}
                      onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                    />

                    <MDBox sx={{ maxHeight: 350, overflowY: "auto", mt: 1 }}>
                      {filteredEleves.map((eleve) => {
                        const selected = form.eleves_ids.includes(eleve.id);
                        return (
                          <MDBox key={eleve.id}
                            onClick={() => toggleEleve(eleve.id)}
                            display="flex" alignItems="center" gap={2}
                            py={1.2} px={2} mb={0.5}
                            sx={{
                              borderRadius: "10px", cursor: "pointer",
                              border: "1px solid",
                              borderColor: selected ? "#00695c" : "#f0f0f0",
                              background: selected ? "#e0f2f1" : "#f8f8f8",
                              transition: "all 0.2s",
                              "&:hover": { background: selected ? "#b2dfdb" : "#f0f0f0" },
                            }}>
                            <MDBox sx={{
                              width: 36, height: 36, borderRadius: "50%",
                              background: selected ? "#00695c" : "#e0e0e0",
                              color: "#fff", display: "flex",
                              alignItems: "center", justifyContent: "center",
                              fontWeight: 700, fontSize: 13, flexShrink: 0,
                              overflow: "hidden",
                            }}>
                              {eleve.photo ? (
                                <img src={eleve.photo} alt=""
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                `${eleve.prenom?.[0] || ""}${eleve.nom?.[0] || ""}`
                              )}
                            </MDBox>
                            <MDBox flex={1}>
                              <MDTypography variant="caption" fontWeight="bold"
                                sx={{ color: "#344767" }}>
                                {eleve.prenom} {eleve.nom}
                              </MDTypography>
                              {eleve.ville && (
                                <MDTypography variant="caption" color="text" display="block">
                                  📍 {eleve.ville}
                                </MDTypography>
                              )}
                            </MDBox>
                            {selected && (
                              <Icon sx={{ color: "#00695c", fontSize: 20 }}>check_circle</Icon>
                            )}
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  </MDBox>

                  {/* Boutons */}
                  <MDBox display="flex" gap={2} justifyContent="flex-end">
                    <MDButton variant="outlined" color="secondary"
                      onClick={() => navigate("/groupes")}>
                      Annuler
                    </MDButton>
                    <MDButton type="submit" disabled={saving} sx={{
                      px: 4, background: "linear-gradient(135deg, #00695c, #00897b)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#004d40" },
                    }}>
                      {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le groupe"}
                    </MDButton>
                  </MDBox>

                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default GroupeForm;